import React, { useState } from 'react';
import { Box, Typography, Button, Alert, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UndoIcon from '@mui/icons-material/Undo';

import GrupoContas from './GrupoContas';
import { GRUPOS } from '../../config/planoContas';
import { repetirMes } from '../../services/janelaDeMeses';

const moeda = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * A página de UM mês: os sete grupos do plano de contas empilhados numa coluna.
 *
 * É o layout que `FecharMes` já usa em produção, e é o que torna 6 e 12 meses
 * viáveis: 32 campos por página em vez de uma grade de até 384 células. O
 * esforço real cai porque a maior parte das contas de uma PME repete —
 * aluguel, contabilidade, salário —, e repetir é um clique.
 *
 * O atalho de repetir SÓ preenche o que está em branco, e só por clique. Valor
 * auto-preenchido que o usuário não confirmou é medição fabricada, que é
 * exatamente o defeito que este produto trata como o mais grave.
 */
const StepMes = ({
  mes, mesAnterior, indice, total, grupos, quantidadeMeses, onChange,
  // O catalogo vem de fora porque ele depende das RESPOSTAS do perfil. O
  // padrao mantem quem abre o questionario sem passar pelo perfil.
  catalogo = GRUPOS
}) => {
  const [copia, setCopia] = useState(null);

  const aplicarCopia = (apenasGrupo = null) => {
    const resultado = repetirMes(grupos, {
      de: mesAnterior.campo,
      para: mes.campo,
      apenasGrupo
    });
    setCopia({
      anterior: grupos,
      preenchidos: resultado.preenchidos,
      mantidos: resultado.mantidos,
      grupo: apenasGrupo
    });
    onChange(resultado.grupos);
  };

  const desfazer = () => {
    onChange(copia.anterior);
    setCopia(null);
  };

  const totalDoMes = catalogo.filter((g) => g.categoria === 'receita').reduce(
    (soma, g) =>
      soma + (grupos[g.id] || []).reduce((s, c) => s + (Number(c[mes.campo]) || 0), 0),
    0
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {mes.longo}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          Mês {indice + 1} de {total} · informe o que entrou e o que saiu neste mês.
        </Typography>
      </Box>

      {mesAnterior && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => aplicarCopia(null)}
            sx={{ minHeight: 44 }}
          >
            Repetir tudo de {mesAnterior.longo}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Preenche só os campos em branco. O que você já digitou fica como está.
          </Typography>
        </Stack>
      )}

      {copia && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<UndoIcon />} onClick={desfazer}>
              Desfazer
            </Button>
          }
        >
          {copia.preenchidos > 0
            ? `${copia.preenchidos} ${copia.preenchidos === 1 ? 'campo preenchido' : 'campos preenchidos'} com os valores de ${mesAnterior.longo}.`
            : `Nenhum campo em branco para preencher com ${mesAnterior.longo}.`}
          {copia.mantidos > 0 &&
            ` ${copia.mantidos} ${copia.mantidos === 1 ? 'campo já tinha valor e foi mantido' : 'campos já tinham valor e foram mantidos'}.`}
        </Alert>
      )}

      {catalogo.map((grupo) => (
        <GrupoContas
          key={grupo.id}
          grupo={grupo}
          contas={grupos[grupo.id] || []}
          mes={mes}
          mesAnterior={mesAnterior}
          quantidadeMeses={quantidadeMeses}
          aoRepetirGrupo={mesAnterior ? aplicarCopia : null}
          onChange={(contas) => onChange({ ...grupos, [grupo.id]: contas })}
        />
      ))}

      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          bgcolor: 'action.hover',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Faturamento de {mes.longo}
        </Typography>
        <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
          {moeda(totalDoMes)}
        </Typography>
      </Box>
    </Box>
  );
};

export default StepMes;
